import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import type { ActionOption, Projection } from '@v2/sdk'
import { createWebGpuRenderer } from '@v2/renderer'
import { TableController, type TableState } from '@v2/table'

declare global { interface Window { __TABLETOP__?: TableController } }

export type TableStatus = { kind: 'starting' } | { kind: 'ready' } | { kind: 'error'; message: string }

/** One controller owns each canvas and all of its GPU resources. React observes coarse UI state. */
export function useTable(canvas: RefObject<HTMLCanvasElement | null>, projection: Projection, onAction: (action: ActionOption) => void, onController?: (controller: TableController | null) => void, onPing?: (position: import("@v2/contracts").Vec3) => void) {
  const controller = useRef<TableController | null>(null)
  const projectionRef = useRef(projection)
  const actionRef = useRef(onAction)
  const pingRef = useRef(onPing)
  pingRef.current = onPing
  const controllerCallback = useRef(onController)
  const errorHandler = useRef<((message: string) => void) | null>(null)
  const [state, setState] = useState<TableState | null>(null)
  const [status, setStatus] = useState<TableStatus>({ kind: 'starting' })
  const [attempt, setAttempt] = useState(0)
  projectionRef.current = projection
  actionRef.current = onAction
  controllerCallback.current = onController

  useEffect(() => {
    const element = canvas.current
    if (!element) return
    let canceled = false
    let failure: string | null = null
    let owned: TableController | null = null
    setStatus({ kind: 'starting' })
    setState(null)
    const release = () => {
      const previous = owned
      owned = null
      if (controller.current === previous) controller.current = null
      if (import.meta.env.DEV && window.__TABLETOP__ === previous) delete window.__TABLETOP__
      previous?.dispose()
      if (previous) controllerCallback.current?.(null)
    }
    const fail = (message: string) => {
      if (canceled || failure !== null) return
      failure = message
      release()
      setState(null)
      setStatus({ kind: 'error', message })
    }
    errorHandler.current = fail
    void createWebGpuRenderer(element, { onError: fail, onDeviceLost: fail }).then(renderer => {
      if (canceled || failure !== null) { renderer.dispose(); return }
      try {
        owned = new TableController(element, renderer, {
          onPing: position => pingRef.current?.(position),
          onAction: action => actionRef.current(action),
          onState: next => { if (!canceled && failure === null) setState(next) },
        })
        controller.current = owned
        if (import.meta.env.DEV) window.__TABLETOP__ = owned
        owned.update(projectionRef.current)
        // Validation callbacks can synchronously fail while the first frame is drawn.
        if (canceled || failure !== null || !owned) { release(); return }
        setState(owned.getState())
        setStatus({ kind: 'ready' })
        controllerCallback.current?.(owned)
      } catch (error) {
        if (!owned) renderer.dispose()
        fail(error instanceof Error ? error.message : String(error))
        release()
      }
    }).catch(error => fail(error instanceof Error ? error.message : String(error)))
    return () => {
      canceled = true
      release()
      if (errorHandler.current === fail) errorHandler.current = null
    }
  }, [canvas, attempt])

  useLayoutEffect(() => {
    try { controller.current?.update(projection) }
    catch (error) { errorHandler.current?.(error instanceof Error ? error.message : String(error)) }
  }, [projection])
  return { controller, state, status, retry: () => setAttempt(current => current + 1) }
}
